import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import SettingsBox from '@medflex/components/stories/SettingsBox';
import { showAlertToast, showSuccessToast } from '@medflex/components/stories/Toast';
import { useLocation } from 'react-router';
import Badge from '@medflex/components/stories/Badge';
import { UserTagTypes } from '@medflex/shared-types';
import Button from '@medflex/components/stories/Button';
import Link from '@medflex/components/stories/Link';
import _ from 'lodash/fp';
import useUserProfile from '../../../../hooks/useUserProfile';
import RequestTagsEdit from './RequestTagsEdit';

const RequestTagsModule = () => {
  const { t } = useTranslation('physician', { keyPrefix: 'InquiryManagementModule.RequestTags' });
  const tagsAnchor = useRef(null);
  const { hash } = useLocation();
  const { userProfile, userTags, editContactPage, userTagGroups } = useUserProfile();
  const widgetSettings = userProfile?.attributes.pages?.widgetSettings;
  const widgetSettingsRequestTagIds = widgetSettings?.requestTagIds || [];
  const [requestTagSelectionRequired, setRequestTagSelectionRequired] = useState(
    widgetSettings?.requestTagSelectionRequired,
  );
  const [isEditing, setIsEditing] = useState(false);

  const handleSubmit = useCallback(
    async ({ requestTagIds, requestTagSelectionRequired }) => {
      try {
        await editContactPage({
          widgetSettings: {
            ..._.omit('requestTagIds', widgetSettings),
            requestTagIds,
            requestTagSelectionRequired,
          },
        });
        setRequestTagSelectionRequired(requestTagSelectionRequired);
        showSuccessToast(t('form.save_successful'));
        setIsEditing(false);
      } catch (e) {
        showAlertToast(t('form.save_failed'));
      }
    },
    [editContactPage, t, widgetSettings],
  );

  const groupedTagOptions = useMemo(
    () =>
      Object.values(
        Object.values(userTags).reduce((acc, tag) => {
          if (tag.type === UserTagTypes.SYSTEM) return acc;
          if (tag.groupId) {
            const tagOption = {
              value: tag.id,
              label: tag.value,
              groupName: userTagGroups[tag.groupId].value,
            };
            acc[tag.groupId] = acc[tag.groupId]?.concat(tagOption) || [tagOption];
            return acc;
          }

          acc[tag.id] = { value: tag.id, label: tag.value };
          return acc;
        }, {}),
      ),
    [userTags, userTagGroups],
  );

  useEffect(() => {
    if (hash.includes('tags') || isEditing) {
      tagsAnchor.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [hash, isEditing]);

  return (
    <div className="mb-5" ref={tagsAnchor}>
      {isEditing && (
        <SettingsBox title={t('headline')} className="text-slate-900">
          <p className="text-slate-900 text-base font-normal">{t('subheadline')}</p>
          <p className="text-slate-600 text-sm mb-4">{t('description')}</p>
          {groupedTagOptions.length === 0 && (
            <div>
              <p className="text-slate-600 text-sm">
                <Trans i18nKey="empty" t={t} className="text-slate-600 text-sm">
                  <Link to="/settings/tags">Hier Tags anlegen.</Link>
                </Trans>
              </p>
              <div className="flex space-x-8 items-center justify-end">
                <Button onClick={() => setIsEditing(false)}>{t('back')}</Button>
              </div>
            </div>
          )}
          {groupedTagOptions.length > 0 && (
            <RequestTagsEdit
              onSubmit={handleSubmit}
              userTagsAndUserTagGroups={groupedTagOptions}
              widgetSettingsRequestTagIds={widgetSettingsRequestTagIds}
              requestTagSelectionRequired={requestTagSelectionRequired}
            />
          )}
        </SettingsBox>
      )}
      {!isEditing && (
        <SettingsBox title={t('headline')} onEdit={() => setIsEditing(true)} canEdit>
          <div className="flex flex-col space-y-2">
            <p className="text-slate-900 text-base font-normal">
              {t('subheadline')}
              {requestTagSelectionRequired && widgetSettingsRequestTagIds.length > 0 && '*'}
            </p>
            {widgetSettingsRequestTagIds.length > 0 && (
              <div className="flex flex-col space-y-2">
                <div className="flex flex-wrap gap-2">
                  {widgetSettingsRequestTagIds.map(tagId => {
                    const tag = userTags[tagId];
                    if (!tag) return null;
                    return (
                      <Badge key={tag.id} className="flex items-center">
                        {tag.value}
                      </Badge>
                    );
                  })}
                </div>
                {requestTagSelectionRequired && (
                  <p className="text-sm font-normal text-slate-600">
                    * {t('disclaimer_mandatory')}
                  </p>
                )}
              </div>
            )}
          </div>
        </SettingsBox>
      )}
    </div>
  );
};

export default RequestTagsModule;
